import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Download, FileText, Filter } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getEncodedHeaderObject, SESSION_KEY } from '../helper/common';

const config = {
  apiUrl: process.env.REACT_APP_API_URL_User,
  currency: 'Rs',
};

const StatementGeneration = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [recentStatements, setRecentStatements] = useState([]);
  const [statementPeriods, setStatementPeriods] = useState([]);
  const navigate = useNavigate();

  // Retrieve custId, cardNo, totalLimit, and availableLimit from localStorage
  const custId = localStorage.getItem('custId');

  const totalLimit = localStorage.getItem('totalLimit');
  const availableLimit = localStorage.getItem('availableLimit');

  useEffect(() => {
    // Check if required fields are available
    if (!custId || !totalLimit || !availableLimit) {
      toast.error('Missing required data. Please return to dashboard.');
      navigate('/Main');
      return;
    }

    const now = new Date();
    const currentValue = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}`;
    const currentLabel = now.toLocaleString('default', { month: 'short', year: 'numeric' });

    const prev = new Date(now);
    prev.setMonth(prev.getMonth() - 1);
    const previousValue = `${prev.getFullYear()}${(prev.getMonth() + 1).toString().padStart(2, '0')}`;
    const previousLabel = prev.toLocaleString('default', { month: 'short', year: 'numeric' });

    setStatementPeriods([
      { value: 'current', label: 'Current Month', date: currentLabel },
      { value: 'previous', label: 'Previous Month', date: previousLabel },
      { value: 'last6', label: 'Last 6 Months', date: 'Select a month' },

    ]);

    // Don't set selectedMonth here, let user select it from dropdown

  }, []);

  const generateLast6Months = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now);
      d.setMonth(d.getMonth() - i);
      const value = `${d.getFullYear()}${(d.getMonth() + 1).toString().padStart(2, '0')}`;
      const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
      months.push({ value, label });
    }
    return months;
  };



  const handleGenerateStatement = async () => {
    setIsGenerating(true);
    try {
      const payload = {
        period: selectedPeriod,
        custId: custId,

        totalLimit: totalLimit,
        availableLimit: availableLimit
      };
      if (selectedPeriod === 'last6') {
        if (!selectedMonth) {
          throw new Error('Please select a month');
        }
        payload.selectedMonth = selectedMonth;
      }
      if (selectedPeriod === 'range') {
        if (!fromDate || !toDate) {
          throw new Error('Please select both from and to dates');
        }
        if (new Date(fromDate) > new Date(toDate)) {
          throw new Error('From date must be before to date');
        }
        payload.fromDate = fromDate;
        payload.toDate = toDate;
      }
      // const custId = localStorage.getItem('custId');
      const token = localStorage.getItem(SESSION_KEY);
      const cnic = localStorage.getItem('cnic');
      const cardNumber = localStorage.getItem('cardNumber');
      const encryptData = await getEncodedHeaderObject(cnic, custId, cardNumber);
      const response = await axios.post(`${config.apiUrl}/api/generate`, payload, {
        headers: {
          'Content-Type': 'application/json',
          // Add Authorization header if required
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      const blob = response.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `statement_${selectedPeriod === 'last6' ? selectedMonth : selectedPeriod}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success('Statement generated and downloaded successfully!');

    } catch (error) {
      let errorMessage = 'Error generating statement';
      if (error.response) {
        // Handle specific HTTP status codes
        if (error.response.status === 400) {
          errorMessage = error.response.data || 'Invalid request data';
          // Convert blob to text if possible
          if (error.response.data instanceof Blob) {
            const text = await error.response.data.text();
            errorMessage = text || errorMessage;
          }
        } else if (error.response.status === 500) {
          errorMessage = 'Server error while generating statement';
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };



  return (
    <div className="py-16 bg-gradient-to-br from-white to-slate-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-4 font-['Inter'] bg-gradient-to-r from-green-600 to-green-700 dark:from-green-400 dark:to-green-500 bg-clip-text text-transparent">View Statement</h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto text-lg">
            Download your credit card statement.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-slate-200/50 hover:shadow-xl hover:bg-white/90 transition-all duration-300 dark:bg-gray-800/80 dark:border-gray-700/50 dark:hover:bg-gray-800/90"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-green-100 dark:bg-green-900/50 p-2 rounded-xl shadow-sm">
                <FileText className="h-5 w-5 text-green-700 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Generate New Statement</h3>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  Select Statement Period
                </label>
                <div className="space-y-2">
                  {statementPeriods.map((period, index) => (
                    <motion.label
                      key={period.value}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.08 }}
                      whileHover={{ x: 4 }}
                      className="flex items-center space-x-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700 p-3 rounded-lg transition-colors"
                    >
                      <input
                        type="radio"
                        name="period"
                        value={period.value}
                        checked={selectedPeriod === period.value}
                        onChange={(e) => setSelectedPeriod(e.target.value)}
                        className="w-4 h-4 text-green-700 dark:text-green-400 border-slate-300 dark:border-gray-600 focus:ring-green-700 dark:focus:ring-green-400"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-900 dark:text-white">{period.label}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{period.date}</div>
                      </div>
                    </motion.label>
                  ))}
                </div>
                {selectedPeriod === 'last6' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Select Month
                    </label>
                    <select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="w-full px-4 py-3 border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-600 dark:focus:ring-green-400 focus:border-transparent transition-all"
                    >
                      <option value="" disabled>Select a month</option>
                      {generateLast6Months().map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

              </div>

              <button
                onClick={handleGenerateStatement}
                disabled={isGenerating || (selectedPeriod === 'last6' && !selectedMonth) || (selectedPeriod === 'range' && (!fromDate || !toDate))}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 dark:from-green-400 dark:to-green-500 text-white py-3 px-4 rounded-xl font-medium hover:from-green-700 hover:to-green-800 dark:hover:from-green-500 dark:hover:to-green-600 focus:outline-none focus:ring-2 focus:ring-green-700 dark:focus:ring-green-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                {isGenerating ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Generating...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>Download Statement</span>
                  </div>
                )}
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-6"
          >


            <div className="space-y-4">
              {recentStatements.map((statement) => (
                <motion.div
                  key={statement.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: statement.id * 0.1 }}
                  className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-xl p-4 hover:shadow-lg hover:bg-white/90 transition-all duration-300 dark:bg-gray-800/80 dark:border-gray-700/50 dark:hover:bg-gray-800/90"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-900 dark:text-white">{statement.period}</h4>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-slate-500 dark:text-slate-400">
                        <span>Generated: {statement.generated}</span>
                        <span>Size: {statement.size}</span>
                      </div>
                    </div>
                    <button

                      className="ml-4 bg-slate-100 dark:bg-gray-700 hover:bg-slate-200 dark:hover:bg-gray-600 text-slate-700 dark:text-slate-300 p-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-700 dark:focus:ring-green-400 shadow-sm hover:shadow-md"
                      aria-label={`Download ${statement.period} statement`}
                    >
                      <Download className="h-4 w-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-yellow-50/80 dark:bg-yellow-900/20 backdrop-blur-sm border border-yellow-200/50 dark:border-yellow-700/50 rounded-xl p-4"
            >
              <div className="flex items-start space-x-3">
                <Filter className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-800 dark:text-yellow-200">Statement Information</h4>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    Statements are generated in PDF format and include all transactions, payments, and fees for the selected period.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default StatementGeneration;